import { consoleLog } from "utils/consoleLog";
import { fetchWithTimeout } from "utils/fetchWithTimeout";

const getStoredAuth = () => localStorage.getItem(window.location.host) || "";

const getAuthHeaders = () => {
  const auth = getStoredAuth();
  return auth ? { Authorization: `Bearer ${auth}` } : {};
};

const getJsonHeaders = () => ({
  "Content-Type": "application/json",
  ...getAuthHeaders(),
});

const getAuthQuery = () => encodeURIComponent(getStoredAuth());

const parseJsonResponse = async (response) => {
  const contentType =
    typeof response.headers?.get === "function"
      ? response.headers.get("content-type")
      : "";

  if (contentType && !contentType.includes("application/json")) {
    return { error: "The server returned an invalid response." };
  }

  try {
    return await response.json();
  } catch (error) {
    return { error: "The server returned an invalid response." };
  }
};

const getResponseError = (json) => json && (json.message || json.error);

export const getCustomPage = async (slug) => {
  const response = await fetch(`/server/page/view/${slug}`);
  const json = await parseJsonResponse(response);

  if (!response.ok) {
    const errorMessage = getResponseError(json);
    consoleLog(errorMessage, "error");
  }

  return json;
};

export const getCustomPages = async () => {
  const response = await fetch("/server/pages/view");
  const json = await parseJsonResponse(response);

  if (!response.ok) {
    const errorMessage = getResponseError(json);
    consoleLog(errorMessage, "error");
  }

  return json;
};

export const addCustomPage = async (pageData) => {
  const response = await fetch("/server/page/new", {
    method: "PUT",
    headers: getJsonHeaders(),
    body: JSON.stringify({ page: pageData }),
  });
  const json = await parseJsonResponse(response);

  if (!response.ok) {
    const errorMessage = getResponseError(json);
    consoleLog(errorMessage, "error");
  }

  return json;
};

export const updateCustomPage = async (updates, docId) => {
  const response = await fetch(`/server/page/update/${docId}`, {
    method: "POST",
    headers: getJsonHeaders(),
    body: JSON.stringify({ updates: updates }),
  });
  const json = await parseJsonResponse(response);

  if (!response.ok) {
    const errorMessage = getResponseError(json);
    consoleLog(errorMessage, "error");
  }

  return json;
};

export const getExtra = async () => {
  const response = await fetch("/server/extra/view");
  const json = await parseJsonResponse(response);

  if (!response.ok) {
    const errorMessage = getResponseError(json);
    consoleLog(errorMessage, "error");
  }

  return json;
};

export const getCategories = async () => {
  const extra = await getExtra();
  return extra.categories;
};

export const getFields = async () => {
  const extra = await getExtra();
  return extra.fields;
};

export const getItems = async () => {
  const response = await fetch("/server/items/view");
  const json = await parseJsonResponse(response);

  if (!response.ok) {
    const errorMessage = getResponseError(json);
    consoleLog(errorMessage, "error");
  }

  return json;
};

export const getItem = async (itemId) => {
  const response = await fetch(`/server/item/view/${itemId}`);
  const json = await parseJsonResponse(response);

  if (!response.ok) {
    const errorMessage = getResponseError(json);
    consoleLog(errorMessage, "error");
  }

  return json;
};

export const addItem = async (itemData) => {
  const response = await fetch("/server/item/new", {
    method: "PUT",
    headers: getJsonHeaders(),
    body: JSON.stringify({ itemData }),
  });
  const json = await parseJsonResponse(response);

  if (!response.ok) {
    const errorMessage = getResponseError(json);
    consoleLog(errorMessage, "error");
  }

  return json;
};

export const updateItem = async (payload, docId) => {
  // eslint-disable-next-line no-unused-vars
  const { _id, ...updates } = payload;

  const response = await fetch(`/server/item/update/${docId}`, {
    method: "POST",
    headers: getJsonHeaders(),
    body: JSON.stringify({ updates }),
  });
  const json = await parseJsonResponse(response);

  if (!response.ok) {
    const errorMessage = getResponseError(json);
    consoleLog(errorMessage, "error");
  }

  return json;
};

export const deleteItem = async (docId) => {
  const response = await fetch(`/server/item/delete/${docId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  const json = await parseJsonResponse(response);

  if (!response.ok) {
    const errorMessage = getResponseError(json);
    consoleLog(errorMessage, "error");
  }

  return json;
};

export const uploadPhoto = async (photo) => {
  const formData = new FormData();
  formData.append("photo", photo);

  const response = await fetch("/server/photo/new", {
    method: "PUT",
    headers: getAuthHeaders(),
    body: formData,
  });
  const json = await parseJsonResponse(response);

  if (!response.ok) {
    const errorMessage = getResponseError(json);
    consoleLog(errorMessage, "error");
  }

  return json;
};

export const deletePhoto = async (filename) => {
  const response = await fetch("/server/photo/delete", {
    method: "DELETE",
    headers: getJsonHeaders(),
    body: JSON.stringify({ filename }),
  });
  const json = await parseJsonResponse(response);

  if (!response.ok) {
    const errorMessage = getResponseError(json);
    consoleLog(errorMessage, "error");
  }

  return json;
};

export const savePhotos = async (photoArray, docId) => {
  const response = await fetch("/server/photos/save", {
    method: "POST",
    headers: getJsonHeaders(),
    body: JSON.stringify({ photos: photoArray, docId: docId }),
  });
  const json = await parseJsonResponse(response);

  if (!response.ok) {
    const errorMessage = getResponseError(json);
    consoleLog(errorMessage, "error");
  }

  return json;
};

export const signIn = async (username, password) => {
  const response = await fetch("/server/auth", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ username, password }),
  });
  const json = await parseJsonResponse(response);

  if (!response.ok) {
    const errorMessage = getResponseError(json);
    consoleLog(errorMessage, "error");
  }

  return json;
};

export const checkIfSignedIn = async () => {
  const response = await fetch(
    `/server/checkAuth?authString=${getAuthQuery()}`,
    {
      headers: getAuthHeaders(),
    }
  );
  const json = await parseJsonResponse(response);

  if (!response.ok) {
    const errorMessage = getResponseError(json);
    consoleLog(errorMessage, "error");
  }

  return json.auth;
};

export const getVisitorIPInfo = async () => {
  const response = await fetchWithTimeout("https://ipapi.co/json/");
  const json = await response.json();

  if (!response.ok) {
    const errorMessage = json && json.message;
    consoleLog(errorMessage, "error");
  }

  return json;
};
